/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { UseToastOptions, useToast } from '@chakra-ui/react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect } from 'react';
import { CustomToast, ShowCustomToastProps } from '@/components/CustomToast';

type UseToastEffectProps = {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  successToast: string;
  errorToast: string;
  // Optional RTK Query error. When present, a server-provided message (e.g. an
  // authorization explanation) is surfaced as the toast description.
  error?: unknown;
};

// Shape of our backend's error payload, carried in `FetchBaseQueryError.data`.
type ServerErrorData = {
  messages?: string[];
  error?: { context?: { message?: string } };
};

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

/**
 * Extract a human-readable message from a backend error response.
 * Backend errors follow the shape `{ title, messages?: string[], error?: { context?: { message } } }`.
 */
export function getServerErrorMessage(error: unknown): string | undefined {
  if (!isFetchBaseQueryError(error) || !('data' in error)) {
    return undefined;
  }
  const data = error.data as ServerErrorData | undefined;
  return data?.error?.context?.message ?? data?.messages?.[0];
}

type UseApiCustomToastProps = UseToastEffectProps & Omit<ShowCustomToastProps, 'variant' | 'message' | 'onCloseCustom'>;

const successToastOptions: UseToastOptions = {
  status: 'success',
  position: 'bottom-right',
  duration: 3000,
  isClosable: true,
};

const errorToastOptions: UseToastOptions = {
  status: 'error',
  position: 'bottom-right',
  duration: 3000,
  isClosable: true,
};

export function useApiToastEffect({
  isLoading,
  isSuccess,
  isError,
  successToast,
  errorToast,
  error,
}: UseToastEffectProps) {
  const toast = useToast();

  // Use success toast effect
  useEffect(() => {
    // If loading is completed
    if (!isLoading) {
      if (isSuccess) {
        toast({ ...successToastOptions, title: successToast });
      } else if (isError) {
        toast({ ...errorToastOptions, title: errorToast, description: getServerErrorMessage(error) });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);
}

export function useCustomToast() {
  const toast = useToast();

  return ({ toastType, variant = 'error', message, buttonOpts }: ShowCustomToastProps) => {
    toast({
      duration: toastType === 'sticky' ? null : 5000,
      position: 'bottom-right',
      render: ({ onClose }) => (
        <CustomToast
          toastType={toastType}
          variant={variant}
          message={message}
          buttonOpts={buttonOpts}
          onCloseCustom={onClose}
        />
      ),
    });
  };
}

export function useApiCustomToast({
  isLoading,
  isSuccess,
  isError,
  successToast,
  errorToast,
  toastType,
  buttonOpts,
}: UseApiCustomToastProps) {
  const toast = useToast();

  useEffect(() => {
    // If loading is completed
    if (!isLoading) {
      if (isSuccess) {
        toast({
          duration: toastType === 'sticky' ? null : 5000,
          position: 'bottom-right',
          render: ({ onClose }) => (
            <CustomToast
              toastType={toastType}
              variant={'success'}
              message={successToast}
              buttonOpts={buttonOpts}
              onCloseCustom={onClose}
            />
          ),
        });
      } else if (isError) {
        toast({
          duration: toastType === 'sticky' ? null : 5000,
          position: 'bottom-right',
          render: ({ onClose }) => (
            <CustomToast
              toastType={toastType}
              variant={'error'}
              message={errorToast}
              buttonOpts={buttonOpts}
              onCloseCustom={onClose}
            />
          ),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);
}
